import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import {
  Fingerprint as FingerprintIcon,
  Security as SecurityIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { startRegistration } from '@simplewebauthn/browser';
import api from '../../utils/api';

interface WebAuthnCredential {
  id: string;
  name: string;
  createdAt: string;
  lastUsed?: string;
  transports?: string[];
  credentialDeviceType: 'singleDevice' | 'multiDevice';
}

interface WebAuthnSetupProps {
  onSetupComplete?: () => void;
  onError?: (error: string) => void;
  showExisting?: boolean;
}

function WebAuthnSetup({ onSetupComplete, onError, showExisting = true }: WebAuthnSetupProps) {
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState<WebAuthnCredential[]>([]);
  const [webAuthnSupported, setWebAuthnSupported] = useState(false);
  const [credentialName, setCredentialName] = useState('');
  const [editDialog, setEditDialog] = useState<{ open: boolean; credential?: WebAuthnCredential }>({ open: false });
  const [newName, setNewName] = useState('');

  useEffect(() => {
    // Check WebAuthn support
    setWebAuthnSupported(
      window.PublicKeyCredential !== undefined &&
      typeof window.PublicKeyCredential === 'function'
    );

    if (showExisting) {
      loadCredentials();
    }
  }, [showExisting]);

  const loadCredentials = async () => {
    try {
      const response = await api.get('/api/webauthn/credentials');
      if (response.data.success) {
        setCredentials(response.data.credentials);
      }
    } catch (error: any) {
      // Don't log error for users without MFA (expected during setup)
      if (error.response?.status !== 401) {
        console.error('Error loading credentials:', error);
      }
      // Set empty credentials array for users without MFA
      setCredentials([]);
    }
  };

  const handleRegisterCredential = async () => {
    if (!webAuthnSupported) {
      onError?.('WebAuthn is not supported in this browser. Please use Chrome, Firefox, Safari, or Edge.');
      return;
    }

    setLoading(true);
    
    try {
      // Start registration
      const optionsResponse = await api.post('/api/webauthn/register/begin');
      const options = optionsResponse.data.options;

      // Use browser WebAuthn API
      const credential = await startRegistration(options);

      // Verify registration
      const verificationResponse = await api.post('/api/webauthn/register/finish', {
        response: credential,
        credentialName: credentialName.trim() || `Authenticator ${new Date().toLocaleDateString()}`
      });

      if (verificationResponse.data.success) {
        setCredentialName('');
        if (showExisting) {
          await loadCredentials();
        }
        onSetupComplete?.();
      } else {
        onError?.('Failed to register authenticator. Please try again.');
      }
    } catch (error: any) {
      console.error('WebAuthn registration error:', error);
      
      let errorMessage = 'Failed to register authenticator';
      let errorDetails = '';
      
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Registration was cancelled or timed out';
        errorDetails = 'Please try again and complete the authentication prompt when it appears.';
      } else if (error.name === 'InvalidStateError') {
        errorMessage = 'This authenticator is already registered';
        errorDetails = 'This device or security key has already been added to your account.';
      } else if (error.name === 'NotSupportedError') {
        errorMessage = 'Biometric authentication not available';
        errorDetails = 'Your device may not have a fingerprint reader, face recognition camera, or Windows Hello configured. You can use a USB security key (like YubiKey) instead, or contact your administrator for assistance.';
      } else if (error.name === 'SecurityError') {
        errorMessage = 'Security error occurred';
        errorDetails = 'This may happen if you\'re not using HTTPS or if your browser security settings block WebAuthn.';
      } else if (error.name === 'AbortError') {
        errorMessage = 'Authentication request was aborted';
        errorDetails = 'Please try again.';
      } else if (error.response?.data?.details) {
        errorMessage = error.response.data.details;
      } else if (error.message) {
        errorDetails = error.message;
      }
      
      const fullMessage = errorDetails ? `${errorMessage}\n\n${errorDetails}` : errorMessage;
      onError?.(fullMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCredential = async (credentialId: string) => {
    try {
      const response = await api.delete(`/api/webauthn/credentials/${credentialId}`);
      if (response.data.success) {
        await loadCredentials();
      }
    } catch (error) {
      console.error('Error deleting credential:', error);
      onError?.('Failed to delete authenticator');
    }
  };

  const handleEditCredential = (credential: WebAuthnCredential) => {
    setNewName(credential.name);
    setEditDialog({ open: true, credential });
  };

  const handleSaveEdit = async () => {
    if (!editDialog.credential || !newName.trim()) return;

    try {
      const response = await api.put(`/api/webauthn/credentials/${editDialog.credential.id}`, {
        name: newName.trim()
      });
      
      if (response.data.success) {
        await loadCredentials();
        setEditDialog({ open: false });
        setNewName('');
      }
    } catch (error) {
      console.error('Error updating credential name:', error);
      onError?.('Failed to update authenticator name');
    }
  };

  const getDeviceTypeIcon = (deviceType: string) => {
    return deviceType === 'multiDevice' ? <SecurityIcon /> : <FingerprintIcon />;
  };

  const getDeviceTypeLabel = (deviceType: string) => {
    return deviceType === 'multiDevice' ? 'Security Key' : 'Platform Authenticator';
  };

  if (!webAuthnSupported) {
    return (
      <Alert severity="warning">
        <Typography variant="body2">
          WebAuthn is not supported in this browser. Please use a modern browser like Chrome, Firefox, Safari, or Edge.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Add New Authenticator */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
          Add New Authenticator
        </Typography>
        
        <TextField
          fullWidth
          label="Authenticator Name (Optional)"
          value={credentialName}
          onChange={(e) => setCredentialName(e.target.value)}
          placeholder="e.g., iPhone Touch ID, YubiKey"
          sx={{ 
            mb: 2,
            '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
            '& .MuiInputBase-input': { color: 'rgba(255, 255, 255, 0.95)' },
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
              '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
              '&.Mui-focused fieldset': { borderColor: '#6366f1' }
            },
            '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' }
          }}
          helperText="Give your authenticator a memorable name"
        />

        <Button
          variant="contained"
          onClick={handleRegisterCredential}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
          fullWidth
        >
          {loading ? 'Setting up...' : 'Add Authenticator'}
        </Button>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Supported authentication methods:</strong>
          </Typography>
          <Typography variant="body2" component="div">
            • <strong>Biometrics:</strong> Fingerprint reader, Face ID, Windows Hello<br/>
            • <strong>Security Keys:</strong> USB keys like YubiKey, Google Titan<br/>
            • <strong>Platform:</strong> Built-in device authenticators
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
            If your device doesn't have biometric hardware, you can use a USB security key instead.
          </Typography>
        </Alert>
      </Box>

      {/* Existing Credentials */}
      {showExisting && credentials.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
            Your Authenticators
          </Typography>
          
          <List>
            {credentials.map((credential) => (
              <ListItem key={credential.id} divider>
                <ListItemIcon>
                  {getDeviceTypeIcon(credential.credentialDeviceType)}
                </ListItemIcon>
                <ListItemText
                  primary={credential.name}
                  secondary={
                    <Box>
                      <Chip 
                        size="small" 
                        label={getDeviceTypeLabel(credential.credentialDeviceType)}
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="caption" display="block" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Added: {new Date(credential.createdAt).toLocaleDateString()}
                        {credential.lastUsed && (
                          <> • Last used: {new Date(credential.lastUsed).toLocaleDateString()}</>
                        )}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    '& .MuiListItemText-primary': { color: 'rgba(255, 255, 255, 0.95)' }
                  }}
                />
                <ListItemSecondaryAction>
                  <IconButton 
                    edge="end" 
                    onClick={() => handleEditCredential(credential)}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    edge="end" 
                    onClick={() => handleDeleteCredential(credential.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false })}>
        <DialogTitle>Edit Authenticator Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false })}>
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default WebAuthnSetup;
